import {
  SearchAdminInvoiceReportsQueryPaymentMethod,
  SearchInvoiceReportsQueryPaymentStatus,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';
import { useGetApCounterpartiesData } from '@/context/services/ap_counterparty/ApCounterparty.service';
import {
  useGetApPayeesData,
  useGetDeptsData,
  useGetOfficeInvoiceReportSettings,
  useGetOfficeMembersData,
} from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';

import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import type { OfficeInvoiceReportSettings } from 'ap-openapi';
import { useEffect, useMemo, useRef, useState } from 'react';

export const useConfigForm = () => {
  const { t } = useTranslation();
  const { data: officeSettings } = useGetOfficeInvoiceReportSettings();
  const deptsExisted = officeSettings?.data?.depts_existed;
  const reportDeptsSetting = officeSettings?.data?.report_depts_setting;

  const paymentStatusOptions = useMemo(
    () => [
      {
        label: t('admin_invoice_reports.search.payment_status.not_yet_paid'),
        value: SearchInvoiceReportsQueryPaymentStatus.not_yet_paid,
        tooltip: t(
          'admin_invoice_reports.search.payment_status.not_yet_paid_tooltip'
        ),
      },
      {
        label: t('admin_invoice_reports.search.payment_status.paid'),
        value: SearchInvoiceReportsQueryPaymentStatus.paid,
        tooltip: t('admin_invoice_reports.search.payment_status.paid_tooltip'),
      },
    ],
    [t]
  );

  const paymentMethodOptions = useMemo(
    () => [
      {
        label: t('admin_invoice_reports.search.payment_method.bank_transfer'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.bank_transfer,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.cash'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.cash,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.payment_slip'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.payment_slip,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.direct_debit'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.direct_debit,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.credit_card'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.credit_card,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.prepaid_card'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.prepaid_card,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.check'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.check,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.promissory_note'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.promissory_note,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.densai'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.densai,
      },
      {
        label: t(
          'admin_invoice_reports.search.payment_method.remittance_abroad'
        ),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.remittance_abroad,
      },
      {
        label: t('admin_invoice_reports.search.payment_method.other'),
        value: SearchAdminInvoiceReportsQueryPaymentMethod.other,
      },
    ],
    [t]
  );

  const labels = useMemo(
    () => ({
      query: {
        text: t('admin_invoice_reports.search.keyword'),
        tooltip: t('admin_invoice_reports.search.keyword_tooltip'),
      },
      number: { text: t('admin_invoice_reports.search.number') },
      status: { text: t('admin_invoice_reports.search.status') },
      applicant: { text: t('admin_invoice_reports.search.applicant') },
      applicant_department: {
        text: t('admin_invoice_reports.search.applicant_department'),
      },
      department: { text: t('admin_invoice_reports.search.department') },
      counterparty: { text: t('admin_invoice_reports.search.counterparty') },
      ap_payee: { text: t('admin_invoice_reports.search.ap_payee') },
      payment_method: {
        text: t('admin_invoice_reports.search.payment_method'),
      },
      total_amount: { text: t('admin_invoice_reports.search.total_amount') },
      payment_status: {
        text: t('admin_invoice_reports.search.payment_status'),
      },
      mf_file: { text: t('admin_invoice_reports.search.mf_file') },
      created_date: { text: t('admin_invoice_reports.search.created_date') },
      submitted_date: {
        text: t('admin_invoice_reports.search.submitted_date'),
      },
      approved_date: {
        text: t('admin_invoice_reports.search.approved_date'),
      },
      book_date: { text: t('admin_invoice_reports.search.book_date') },
      due_date: { text: t('admin_invoice_reports.search.due_date') },
      payment_date: { text: t('admin_invoice_reports.search.payment_date') },
      reset_button: t('admin_invoice_reports.search.reset_button'),
      search_button: t('admin_invoice_reports.search.search_button'),
    }),
    [t]
  );

  const selectProps = useMemo(
    () => ({
      size: 'small' as const,
      apPayee: {
        inputPlaceholder: t(
          'admin_invoice_reports.search.ap_payee.placeholder'
        ),
        size: 'small' as const,
      },
      applicant: {
        maxTagCount: 1,
        placeholder: '',
        inputPlaceholder: t(
          'admin_invoice_reports.search.applicant.placeholder'
        ),
        size: 'small' as const,
      },
      applicantDepartment: {
        maxTagCount: 1,
        placeholder: '',
        inputPlaceholder: t(
          'admin_invoice_reports.search.applicant_department.placeholder'
        ),
        size: 'small' as const,
      },
      department: {
        maxTagCount: 1,
        disabled: !reportDeptsSetting?.enabled,
        placeholder: '',
        inputPlaceholder: t(
          'admin_invoice_reports.search.department.placeholder'
        ),
        size: 'small' as const,
      },
      counterparty: {
        inputPlaceholder: t(
          'admin_invoice_reports.search.counterparty.placeholder'
        ),
        size: 'small' as const,
      },
    }),
    [t, reportDeptsSetting]
  );

  const inputProps = useMemo(
    () => ({
      query: { search: true, size: 'small' as const },
      number: { search: false, size: 'small' as const },
    }),
    []
  );

  const deptDefaultOptions = useMemo(() => {
    const raw = reportDeptsSetting?.enabled
      ? reportDeptsSetting?.blank_report_dept_option
      : undefined;
    return raw ? [{ label: raw.label || '', value: raw.id || '' }] : undefined;
  }, [reportDeptsSetting]);

  const officeInvoiceReportSettings: OfficeInvoiceReportSettings | undefined =
    officeSettings?.data;

  return {
    labels,
    selectProps,
    inputProps,
    paymentStatusOptions,
    paymentMethodOptions,
    deptsExisted,
    reportDeptsSetting,
    deptDefaultOptions,
    officeInvoiceReportSettings,
  };
};

const useInitialData = <T extends BaseOption>(
  ids: string[],
  fetchFunction: (ids: string[]) => Promise<T[]>
) => {
  const [options, setOptions] = useState<T[]>([]);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    if (ids.length === 0) return;

    const fetchData = async () => {
      try {
        const data = await fetchFunction(ids);
        setOptions(data);
      } catch {
        setOptions([]);
      }
    };
    fetchData();
  }, [ids, fetchFunction]);

  return { options };
};

export const useOfficeMembersInitialData = (memberIds: string[]) => {
  const { fetchOfficeMembers } = useGetOfficeMembersData();
  const { options } = useInitialData(memberIds, fetchOfficeMembers);
  return options;
};

export const useDeptInitialData = (deptIds: string[]) => {
  const { fetchDepts } = useGetDeptsData();
  const { options } = useInitialData(deptIds, fetchDepts);
  return options;
};

export const useApCounterpartyInitialData = (counterpartyIds: string[]) => {
  const { fetchApCounterparties } = useGetApCounterpartiesData();
  const { options } = useInitialData(counterpartyIds, fetchApCounterparties);
  return options;
};

export const useApPayeeInitialData = (payeeIds: string[]) => {
  const { fetchApPayees } = useGetApPayeesData();
  const { options } = useInitialData(payeeIds, fetchApPayees);
  return options;
};
